package com.dotdrives.model

import kotlin.time.Instant
import kotlinx.serialization.Serializable

/** Status values from `reports.status` (docs/schema.md: "Updates and reports"). */
object ReportStatus {
    const val DRAFT = "draft"
    const val SUBMITTED = "submitted"
    const val SHARED = "shared"
}

/** One row-worth of list data: `reports` enriched with target/assignee names, the latest
 *  `report_updates` preview, a `files` count, and this viewer's `read_states` unread flag. */
@Serializable
data class ReportSummary(
    val id: String,
    val title: String,
    val targetId: String?,
    val targetName: String?,
    val assigneeUserId: String,
    val assigneeName: String?,
    val status: String,
    val latestPreview: String,
    val latestAt: Instant?,
    val fileCount: Int,
    val isUnread: Boolean,
)

/** `reports`, enriched with target/assignee display names for the thread header. */
data class Report(
    val id: String,
    val orgId: String,
    val title: String,
    val targetId: String?,
    val targetName: String?,
    val assigneeUserId: String,
    val assigneeName: String?,
    val status: String,
    val dueAt: Instant?,
    val submittedAt: Instant?,
    val createdAt: Instant,
)

/** `report_updates`, enriched with the author's display name. `authorUserId == null` means
 *  `viaEmail` (docs/schema.md: "null when via_email"). This table has no `_update` policy --
 *  a posted update is immutable by design; there is deliberately no edit path anywhere above. */
data class ReportUpdate(
    val id: String,
    val reportId: String,
    val body: String,
    val authorUserId: String?,
    val authorName: String?,
    val viaEmail: Boolean,
    val createdAt: Instant,
)

/** `report_default_updates` -- org-scoped templates used to seed a new report's first update. */
data class ReportDefaultUpdate(
    val id: String,
    val subject: String,
    val message: String,
)

/** A `files` row where `owner_type = 'report'`. */
data class ReportFileItem(
    val id: String,
    val fileName: String,
    val fileSize: Long,
    val createdAt: Instant,
)

data class NewReport(
    val title: String,
    val targetId: String?,
    val assigneeUserId: String,
)

/** The minimal giver fields the share-with-givers audience picker needs. */
data class GiverLite(
    val id: String,
    val displayName: String,
)

/**
 * A preset audience for the share-with-givers dialog (`design/portal-updates.dc.html`'s share
 * dialog names two live-counted presets plus a manual pick). Both presets are expressible on
 * the shipped schema and are staff-only queries in practice: `report_recipients` insert (the
 * only thing this dialog can ever do) is gated on `has_perm(org_id,'report.write')`, which
 * excludes `field` entirely, so the `donation.read`/`drive.read` RLS a field rep might lack is
 * never a barrier for anyone who can open this dialog in the first place.
 */
data class ShareAudience(
    val giverIds: List<String>,
    /** Only meaningful for the target audience -- givers in [giverIds] with an active
     *  `giving_subscriptions` row ("includes 38 monthly partners" in the design copy). */
    val monthlyPartnerCount: Int = 0,
)

@Serializable
data class UpdatesCursor(val createdAt: Instant, val id: String)

@Serializable
data class UpdatesBoardPage(
    val reports: List<ReportSummary> = emptyList(),
    val totalCount: Int = 0,
    val unreadCount: Int = 0,
    val draftCount: Int = 0,
    val submittedCount: Int = 0,
    val sharedCount: Int = 0,
    val matchingCount: Int = 0,
    val nextCursor: UpdatesCursor? = null,
)
